import { publicApi } from "@/lib/api/public-client";
import { orderApi } from "@/lib/api/order-bf-client";
import { publicEndpoints } from "@/lib/endpoints";
import { AddToCartResponse, CartVendorTotalsResponse, ShoppingCart, ShoppingCartConfirm, ShoppingCartItem, ShoppingCartType } from "@/lib/types/cart";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { snakeToCamelCaseObject } from "@/lib/utils";


const addProductToCart = async (productId: number, quantity: number, shoppingCartType: ShoppingCartType) => {
    const endpoint = publicEndpoints.cart.addToCart(productId, shoppingCartType, quantity);
    const response = await publicApi.post<AddToCartResponse>(endpoint);
    return response.data;
}

const getCart = async () => {
    const endpoint = publicEndpoints.cart.getCart;
    const response = await publicApi.get<unknown>(endpoint);
    return snakeToCamelCaseObject<ShoppingCart>(response.data);
}

const getConfirmOrder = async () => {
    const endpoint = publicEndpoints.orders.confirmOrder;
    const response = await publicApi.get<unknown>(endpoint);
    return snakeToCamelCaseObject<ShoppingCartConfirm>(response.data);
}


const updateCartItemQuantity = async (
    items: ShoppingCartItem[] | null | undefined,
    updatedItemId: number,
    updatedQuantity: number
) => {
    const payload: Record<string, string> = { "removefromcart": "" };
    if (items && items.length > 0) {
        for (const item of items) {
            const key = `itemquantity${item.id}`;
            const value = item.id === updatedItemId ? updatedQuantity : item.quantity;
            payload[key] = value.toString();
        }
    } else {
        const key = `itemquantity${updatedItemId}`;
        payload[key] = updatedQuantity.toString();
    }
    const endpoint = publicEndpoints.cart.updateCart;
    const response = await publicApi.post<unknown>(endpoint, payload);
    return snakeToCamelCaseObject<ShoppingCart>(response.data);
}

const removeCartItem = async (
    items: ShoppingCartItem[] | null | undefined,
    itemIdToRemove: number
) => {
    const payload: Record<string, string> = {
        "removefromcart": itemIdToRemove.toString(),
    };

    if (items && items.length > 0) {
        for (const item of items) {
            if (item.id === itemIdToRemove) continue;
            const key = `itemquantity${item.id}`;
            payload[key] = item.quantity.toString();
        }
    }

    const endpoint = publicEndpoints.cart.updateCart;
    const response = await publicApi.post<unknown>(endpoint, payload);
    return snakeToCamelCaseObject<ShoppingCart>(response.data);
}

export const useAddProductToCart = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: { productId: number, quantity: number, shoppingCartType: ShoppingCartType }) => {
            return addProductToCart(payload.productId, payload.quantity, payload.shoppingCartType);
        },
        // Optimistically bump quantity if the product already exists in the cart
        onMutate: async (payload) => {
            await queryClient.cancelQueries({ queryKey: ['cart'] });
            const previousCart = queryClient.getQueryData<ShoppingCart>(['cart']);
            if (previousCart && previousCart.items) {
                const nextItems = previousCart.items.map((item) => {
                    if (item.productId === payload.productId) {
                        const nextQuantity = item.quantity + payload.quantity;
                        return {
                            ...item,
                            quantity: nextQuantity,
                            subTotalValue: item.unitPriceValue * nextQuantity,
                        };
                    }
                    return item;
                });
                queryClient.setQueryData<ShoppingCart>(['cart'], {
                    ...previousCart,
                    items: nextItems,
                });
            }
            return { previousCart } as { previousCart?: ShoppingCart };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousCart) {
                queryClient.setQueryData(['cart'], context.previousCart);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'], refetchType: 'all' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'], refetchType: 'all' });
        },
    });
}

export const useUpdateCartItemQuantity = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: { itemId: number, quantity: number }) => {
            const currentCart = queryClient.getQueryData<ShoppingCart>(['cart']);
            return updateCartItemQuantity(currentCart?.items, payload.itemId, payload.quantity);
        },
        onMutate: async (payload) => {
            await queryClient.cancelQueries({ queryKey: ['cart'] });
            const previousCart = queryClient.getQueryData<ShoppingCart>(['cart']);
            if (previousCart && previousCart.items) {
                const nextItems = previousCart.items.map((item) => {
                    if (item.id === payload.itemId) {
                        const nextQuantity = payload.quantity;
                        return {
                            ...item,
                            quantity: nextQuantity,
                            subTotalValue: item.unitPriceValue * nextQuantity,
                        };
                    }
                    return item;
                });
                queryClient.setQueryData<ShoppingCart>(['cart'], {
                    ...previousCart,
                    items: nextItems,
                });
            }
            return { previousCart } as { previousCart?: ShoppingCart };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousCart) {
                queryClient.setQueryData(['cart'], context.previousCart);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'], refetchType: 'all' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'], refetchType: 'all' });
        },
    });
}

export const useRemoveCartItem = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: { itemId: number }) => {
            const currentCart = queryClient.getQueryData<ShoppingCart>(['cart']);
            return removeCartItem(currentCart?.items, payload.itemId);
        },
        onMutate: async (payload) => {
            await queryClient.cancelQueries({ queryKey: ['cart'] });
            const previousCart = queryClient.getQueryData<ShoppingCart>(['cart']);
            if (previousCart && previousCart.items) {
                const nextItems = previousCart.items.filter((item) => item.id !== payload.itemId);
                queryClient.setQueryData<ShoppingCart>(['cart'], {
                    ...previousCart,
                    items: nextItems,
                });
            }
            return { previousCart } as { previousCart?: ShoppingCart };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousCart) {
                queryClient.setQueryData(['cart'], context.previousCart);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'], refetchType: 'all' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'], refetchType: 'all' });
        },
    });
}

const removeVendorItems = async (
    items: ShoppingCartItem[] | null | undefined,
    vendorName: string
) => {
    console.log('removeVendorItems called with vendor:', vendorName);
    console.log('Current cart items:', items);
    
    if (!items || items.length === 0) {
        console.log('No items in cart, fetching fresh cart');
        const endpoint = publicEndpoints.cart.getCart;
        const response = await publicApi.get<unknown>(endpoint);
        return snakeToCamelCaseObject<ShoppingCart>(response.data);
    }

    // Find all items for this vendor
    const vendorItemIds = items
        .filter(item => item.vendorName === vendorName)
        .map(item => item.id);

    console.log('Vendor item IDs to remove:', vendorItemIds);

    if (vendorItemIds.length === 0) {
        console.log('No items found for vendor, fetching fresh cart');
        const endpoint = publicEndpoints.cart.getCart;
        const response = await publicApi.get<unknown>(endpoint);
        return snakeToCamelCaseObject<ShoppingCart>(response.data);
    }

    // Build payload to remove ALL vendor items
    // Backend expects comma-separated item IDs in the "removefromcart" field
    const payload: Record<string, string> = {
        "removefromcart": vendorItemIds.join(',')
    };

    // Keep quantities for items that are NOT from this vendor
    const remainingItems = items.filter(item => item.vendorName !== vendorName);
    for (const item of remainingItems) {
        const key = `itemquantity${item.id}`;
        payload[key] = item.quantity.toString();
    }

    console.log('Payload for updateCart:', payload);

    const endpoint = publicEndpoints.cart.updateCart;
    const response = await publicApi.post<unknown>(endpoint, payload);
    console.log('UpdateCart response:', response.data);
    return snakeToCamelCaseObject<ShoppingCart>(response.data);
}

export const useRemoveVendorItems = () => {
    const queryClient = useQueryClient();
    let capturedCartItems: ShoppingCartItem[] | null | undefined;
    
    return useMutation({
        mutationFn: async (payload: { vendorName: string }) => {
            // Use the captured cart items from onMutate
            return removeVendorItems(capturedCartItems, payload.vendorName);
        },
        onMutate: async (payload) => {
            await queryClient.cancelQueries({ queryKey: ['cart'] });
            const previousCart = queryClient.getQueryData<ShoppingCart>(['cart']);
            
            // Capture the cart items BEFORE optimistic update
            capturedCartItems = previousCart?.items;
            
            if (previousCart && previousCart.items) {
                const nextItems = previousCart.items.filter(
                    (item) => item.vendorName !== payload.vendorName
                );
                queryClient.setQueryData<ShoppingCart>(['cart'], {
                    ...previousCart,
                    items: nextItems,
                });
            }
            return { previousCart } as { previousCart?: ShoppingCart };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousCart) {
                queryClient.setQueryData(['cart'], context.previousCart);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'], refetchType: 'all' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'], refetchType: 'all' });
        },
    });
}

export const useGetCart = (options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: ['cart'],
        queryFn: getCart,
        enabled: options?.enabled ?? true,
    });
}

export const useGetCartConfirm = (shippingSelected: boolean) => {
    return useQuery({
        queryKey: ['checkoutOrder', shippingSelected],
        queryFn: getConfirmOrder,
        enabled: true,
        staleTime: 0,
        gcTime: 0
    });
};

const getVendorTotals = async () => {
    const endpoint = publicEndpoints.cart.getVendorTotals;
    const response = await orderApi.get<CartVendorTotalsResponse>(endpoint);
    return response.data;
}

export const useGetVendorTotals = (options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: ['vendorTotals'],
        queryFn: getVendorTotals,
        enabled: options?.enabled ?? true,
        staleTime: 5000, // Cache for 5 seconds
    });
};

export const useApplyDiscountCoupon = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (code: string) => {
            const endpoint = publicEndpoints.cart.applyDiscount(code);
            // The API requires a non-empty request body even though the coupon code
            // is already passed as a query parameter (mirrors nopCommerce form binding).
            const response = await publicApi.post<unknown>(endpoint, { discountcouponcode: code });
            return snakeToCamelCaseObject<ShoppingCart>(response.data);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'], refetchType: 'all' });
            queryClient.invalidateQueries({ queryKey: ['checkoutOrder'], refetchType: 'all' });
            queryClient.invalidateQueries({ queryKey: ['vendorTotals'], refetchType: 'all' });
        },
    });
};

export const useRemoveDiscountCoupon = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (discountId: number) => {
            const endpoint = publicEndpoints.cart.removeDiscount;
            // The endpoint requires a form body with the key "removediscount-{id}" to identify
            // which discount to remove (mirrors nopCommerce ShoppingCartController convention).
            const body = { [`removediscount-${discountId}`]: '' };
            const response = await publicApi.post<unknown>(endpoint, body);
            return snakeToCamelCaseObject<ShoppingCart>(response.data);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'], refetchType: 'all' });
            queryClient.invalidateQueries({ queryKey: ['checkoutOrder'], refetchType: 'all' });
            queryClient.invalidateQueries({ queryKey: ['vendorTotals'], refetchType: 'all' });
        },
    });
};



